import { defineStore } from 'pinia'
import pinia from '@src/plugins/pinia'
import axios from '@src/libs/axios'
import useWebStore from '@src/stores/web'
import useLayoutStore from '@src/stores/layout'
import { views } from '@src/modules'
import type { User } from '../models/User'
import type { Route } from '../models/Route'
import type { Resource, ResourceArray } from '../types/resource'
import { requestNotificationPermission } from '@src/libs/webpush'
import {
    loginWithPassword,
    exchangeCode,
    revokeSession,
    refreshAccessToken,
    clearSanctumSession,
    isSanctumSession,
    refreshSanctumSession,
    getAccessTokenClaims,
    CLIENT_ID,
    ADMIN_CLIENT_ID,
} from '@src/libs/authClient'

/**
 * O auth server responde erros no formato OAuth (`{error, error_description}`),
 * mas a tela de login (Login/index.ts) já sabe ler o formato do Laravel
 * (`{message}` para 403/429/5xx, `{errors: {password: [...]}}` para
 * 401/422); normaliza aqui pra não precisar mexer na tela.
 */
function normalizeAuthError(error: any) {
    const data = error?.response?.data
    if (data && typeof data === 'object' && 'error' in data) {
        const message = data.error_description || data.error
        error.response.data = {
            message,
            errors: { password: [message] },
        }
    }
    return error
}

/**
 * Nenhum usuário tem TOTP cadastrado ainda (a tabela é nova, no auth server):
 * então este ramo não deveria disparar na prática hoje. Fica como uma trava
 * explícita em vez de silenciosamente travar em outro lugar quando alguém
 * cadastrar o primeiro segundo fator.
 */
function mfaNotSupportedError() {
    return {
        response: {
            status: 403,
            data: {
                message:
                    'Login com verificação em duas etapas ainda não é suportado. Contate o suporte.',
            },
        },
    }
}

export type UserState = {
    user: User | null
    businessId: Number | null

    permissions: string[]
    routeItems: Route[]
    notifications: []
    loadingRoutes: boolean
    hasMultipleSystems: boolean
    isAdmin: boolean
    isMaster: boolean
    isLoggedIn: boolean
    modules: RequestModules
}

type RequestModules = {
    data: string[]
    avaliable: string[]
}

/**
 * Permissões que descrevem o estado da impersonação. Não são permissões reais do
 * usuário, então não podem ser concedidas automaticamente para donos do sistema.
 */
const IMPERSONATION_PERMISSIONS = ['logout-partner', 'impersonating-user']

const useUserStore = defineStore('user', {
    state: () =>
        ({
            user: null,
            businessId: null,
            permissions: [],
            routeItems: [],
            notifications: [],
            loadingRoutes: false,

            // boolean vars
            hasMultipleSystems: false,
            isAdmin: false,
            isMaster: false,
            isLoggedIn: false,
            modules: { data: [], avaliable: [] }, //Módulos que o cliente logado tem acesso
        }) as UserState,
    getters: {
        /**
         * Sessão navegando como o business parceiro, sem um business_user real por trás.
         */
        isFatherBusinessSession(): boolean {
            return (
                this.permissions.includes('logout-partner') &&
                !this.permissions.includes('impersonating-user')
            )
        },
        /**
         * Sessão que chegou até aqui por impersonação e pode voltar um nível.
         */
        isImpersonating(): boolean {
            return this.permissions.includes('logout-partner')
        },
    },
    actions: {
        hasPermission(permissions: string | string[]) {
            const isOwner =
                this.permissions.includes('owner') ||
                this.permissions.includes('*')
            if (Array.isArray(permissions)) {
                return permissions.some((permission) => {
                    if (
                        !IMPERSONATION_PERMISSIONS.includes(permission) &&
                        isOwner
                    )
                        return true
                    return this.permissions.includes(permission)
                })
            }
            if (!IMPERSONATION_PERMISSIONS.includes(permissions) && isOwner)
                return true
            return this.permissions.includes(permissions)
        },
        hasModule(modules: string | string[]) {
            if (Array.isArray(modules)) {
                return this.modules.data.some((module) => {
                    return modules.includes(module)
                })
            }
            return this.modules.data.includes(modules)
        },
        /**
         * Troca o business ativo via o auth server (`business_id` no grant
         * `refresh_token`): o access_token novo já vem com
         * `business_id`/`permissions`/`modules` certos nas claims, sem round-
         * trip extra ao Laravel pra "gravar" a troca (não existe mais onde
         * gravar: não há personal_access_token por trás de uma sessão JWT).
         */
        async switchBusiness(businessId: string) {
            if (isSanctumSession()) {
                await refreshSanctumSession(businessId)
            } else {
                await refreshAccessToken(undefined, businessId)
            }
            await this.updateBusiness(businessId as unknown as Number)
        },
        async updateBusiness(businessId: Number | null) {
            if (this.businessId == businessId && businessId != null) return
            this.businessId = businessId
            if (businessId == null) {
                this.permissions = []
                this.modules = { data: [], avaliable: [] }
                return await Promise.all([this.updateRoutes()])
            }
            await Promise.all([
                this.updateModules(),
                this.updatePermissions(),
                this.updateRoutes(),
            ])
        },
        async updatePermissions() {
            if (!this.isLoggedIn || this.isAdmin) {
                this.permissions = ['any']
                return
            }
            /**
             * O access_token JWT já carrega as permissões nas claims, evita o
             * round-trip ao Laravel quando a sessão é JWT. Sessão Sanctum não
             * tem o token acessível ao JS (cookie httpOnly), então segue
             * pedindo ao backend.
             */
            if (!isSanctumSession()) {
                const claims = getAccessTokenClaims()
                if (claims?.permissions) {
                    this.permissions = claims.permissions
                    return
                }
            }
            const webStore = useWebStore(pinia)
            try {
                this.permissions = (
                    await webStore.get<{
                        data: string[]
                    }>({
                        url: 'core/info/permissions',
                        locale: '[Get my Permissions]',
                        silent: true,
                    })
                ).data.data
            } catch (e) {}
        },
        /**
         * @param includeAvailable a claim `modules` do JWT só tem os módulos
         * ativos do business: o catálogo completo (`avaliable`), usado só na
         * tela de configuração (SettingDrawer), não existe no token e precisa
         * sempre vir do backend.
         */
        async updateModules(includeAvailable = false) {
            if (!this.isLoggedIn || this.isAdmin) {
                return
            }
            if (!includeAvailable && !isSanctumSession()) {
                const claims = getAccessTokenClaims()
                if (claims?.modules) {
                    this.modules = {
                        data: claims.modules,
                        avaliable: this.modules.avaliable,
                    }
                    return
                }
            }
            const webStore = useWebStore(pinia)
            try {
                this.modules = (
                    await webStore.get<RequestModules>({
                        url: 'core/info/business/modules',
                        locale: '[Get Modules from Business of User]',
                        silent: true,
                    })
                ).data
            } catch (e) {
                this.modules = { data: [], avaliable: [] }
            }
        },
        async loginAdmin(user: User) {
            let code: string
            try {
                const result = await loginWithPassword(
                    user.email,
                    user.password ?? '',
                    ADMIN_CLIENT_ID
                )
                if ('mfaRequired' in result) throw mfaNotSupportedError()
                await exchangeCode(result.code, ADMIN_CLIENT_ID)
            } catch (error) {
                throw normalizeAuthError(error)
            }
            clearSanctumSession()

            const response =
                await axios.get<Resource<User>>('/api/auth/me/admin')
            requestNotificationPermission()
            this.user = response.data.data
            this.isAdmin = true
            this.isLoggedIn = true
            this.businessId = null
            return response
        },
        async loginUser(user: User) {
            let code: string
            try {
                const result = await loginWithPassword(
                    user.email,
                    user.password ?? '',
                    CLIENT_ID
                )
                if ('mfaRequired' in result) throw mfaNotSupportedError()
                await exchangeCode(result.code, CLIENT_ID)
            } catch (error) {
                throw normalizeAuthError(error)
            }
            clearSanctumSession()

            const response = await axios.get<
                Resource<User> & { admin: boolean }
            >('/api/auth/me')
            requestNotificationPermission()
            this.user = response.data.data
            this.isAdmin = false
            this.isLoggedIn = true
            this.isMaster = response.data.admin
            this.businessId = null
            await this.updateBusiness(null)
            return response
        },
        async logout() {
            if (this.hasPermission('logout-partner')) {
                const wasImpersonatingUser =
                    this.hasPermission('impersonating-user')
                const url = '/api/partnersystem/logout'
                const response = await axios.post<{
                    business: Number
                    user?: User
                    impersonation_depth: number
                }>(url)
                if (wasImpersonatingUser) {
                    /**
                     * O dono do token voltou a ser outro usuário, então praticamente
                     * todo o estado da aplicação muda. Recarregar é mais seguro e
                     * espelha o que a entrada em outro usuário já faz: o reload
                     * também é o que reativa o JWT original (authClient reinicia
                     * do zero e busca um token novo via refreshToken() no boot).
                     */
                    if (response.data.user) {
                        this.user = response.data.user
                    }
                    window.location.href = '/'
                    return
                }
                /**
                 * Voltou à profundidade 0: saiu de vez da impersonação. O token
                 * Sanctum "ponte" (ver authClient.setSanctumOverride) deixa de
                 * valer: daqui pra frente as requests voltam a usar o JWT
                 * original, que continuou intacto em memória o tempo todo.
                 */
                if (response.data.impersonation_depth === 0) {
                    clearSanctumSession()
                }
                window.router.push('/')
                await Promise.all([
                    this.updateBusiness(response.data.business),
                    useLayoutStore().updateTheme(),
                ])
                return
            }
            const wasAdmin = this.isAdmin
            const wasSanctumSession = isSanctumSession()
            this.isAdmin = false
            this.isLoggedIn = false
            this.user = null
            this.businessId = null
            this.modules = { data: [], avaliable: [] }
            await this.updateRoutes() // Only to clear current routes and refresh router
            window.router.push(wasAdmin ? '/login/admin' : '/login/user')

            /**
             * Sessão Sanctum (login por link, convite) não existe no auth
             * server: quem a encerra é o backend, apagando o token e expirando
             * o cookie, que é httpOnly e portanto fora do alcance daqui.
             */
            if (wasSanctumSession) {
                return await axios
                    .post('/api/auth/logout')
                    .catch((error) => {
                        return error
                    })
                    .finally(() => {
                        clearSanctumSession()
                    })
            }

            return await revokeSession().catch((error) => {
                return error
            })
        },
        /**
         * Renova o access_token via o auth server. `clientId` segue o realm
         * persistido (`isAdmin`), não o que sobrou em memória: depois de um
         * reload de página o módulo authClient reinicia do zero e não tem
         * como adivinhar sozinho qual dos dois realms era a sessão ativa.
         */
        async refreshToken() {
            /**
             * Sessão Sanctum (autologin por link, convite, impersonação) não
             * existe no auth server: renovar contra ele derrubaria a sessão no
             * boot depois de um F5. Quem estica a validade é o backend, via
             * `PUT /api/auth/refresh`, e o cookie novo vem na resposta.
             */
            if (isSanctumSession()) {
                await refreshSanctumSession()
            } else {
                const clientId = this.isAdmin ? ADMIN_CLIENT_ID : CLIENT_ID
                await refreshAccessToken(clientId)
            }

            const response = await axios.get<Resource<User | null>>(
                this.isAdmin ? '/api/auth/me/admin' : '/api/auth/me'
            )
            if (response.data.data) this.user = response.data.data
            this.isLoggedIn = true

            return response
        },
        async updateRoutes() {
            function isMobileDevice() {
                const isMobileUA = /Mobi|Android|iPhone|iPad|iPod/i.test(
                    navigator.userAgent
                )
                const isSmallScreen =
                    window.matchMedia('(max-width: 768px)').matches
                return isMobileUA || isSmallScreen
            }
            const webStore = useWebStore(pinia)
            const browseable = function (routes: Route[]) {
                const checkitems = (routes: Route[]) => {
                    return routes
                        .map((r: Route) => {
                            let rot = r.routes
                            if (r.routes) rot = checkitems(r.routes)
                            const route = {
                                ...r,
                                can_mobile:
                                    r.can_mobile ||
                                    r.routes?.some((e) => e.can_mobile),
                                can_desktop:
                                    r.can_desktop ||
                                    r.routes?.some((e) => e.can_desktop),
                                routes: rot,
                            } as Route
                            return route
                        })
                        .filter(
                            (r) => !(r.routes && r.routes.length === 0)
                        ) as Route[]
                }
                const newRoutes = checkitems(routes)
                return newRoutes
            }

            if (!this.isLoggedIn) {
                if (window.router) window.updateRouter()
                return
            }
            this.loadingRoutes = true
            if (this.isAdmin) {
                var localRouteItems = [] as (Route & { position: number })[]
                const result = await Promise.all(
                    views['Admin'].map(async (element: any) => {
                        return await element.import()
                    })
                )
                for (const view of result as any[]) {
                    const page = view.default
                    let route = {
                        name: page.name,
                        location: 'Admin@' + page.name,
                        title: 'Admin.routes.' + page.name,
                        icon: page.icon || 'mdi-account-question-outline ',
                        to: page.path || '/admin/' + page.name,
                        path: page.path || '/admin/' + page.name,
                        position: page.position || localRouteItems.length,
                        can_mobile: page.can_mobile || true,
                        can_desktop: page.can_desktop || true,
                    } as Route & { position: number }

                    if (page.fatherDir) {
                        let father = localRouteItems.find((aux) => {
                            return aux.title == 'Admin.routes.' + page.fatherDir
                        })
                        if (father) {
                            if (!father.routes) father.routes = []
                            father.position = Math.min(
                                father.position,
                                route.position
                            )
                            father.routes.push(route)
                        } else {
                            father = {
                                title: 'Admin.routes.' + page.fatherDir,
                                icon: 'mdi-folder',
                                routes: [route],
                                name: 'Admin.routes.' + page.fatherDir,
                                path: '/admin/' + page.fatherDir,
                                position: route.position,
                                can_mobile: page.can_mobile || true,
                                can_desktop: page.can_desktop || true,
                            } as Route & { position: number }
                            localRouteItems.push(father)
                        }
                    } else {
                        localRouteItems.push(route)
                    }
                }
                localRouteItems.sort((a, b) => {
                    return a.position - b.position
                })

                this.routeItems = browseable(localRouteItems)
            } else {
                try {
                    const response = await webStore.get<ResourceArray<Route>>({
                        url: 'core/info/routes',
                        locale: '[Get Routes User]',
                    })
                    this.routeItems = browseable(response.data.data)
                } catch (response) {
                    this.routeItems = []
                }
            }

            if (window.router) window.updateRouter()
            this.loadingRoutes = false
        },
    },
    persist: true,
})

export default useUserStore
